/**
 * Iter 193 — Client Health Card widget.
 *
 * Compact one-pager summarising the last 7 days for a project.
 * Renders as a section on the Project Overview tab.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import {
  HeartPulse, Users, ShieldAlert, Package, ClipboardCheck,
  Loader2, ExternalLink, PenSquare, ArrowLeftRight, Bot,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";

const HEALTH_TONE = {
  on_track: "border-emerald-500 text-emerald-800 bg-emerald-50",
  at_risk: "border-amber-500 text-amber-800 bg-amber-50",
  off_track: "border-rose-500 text-rose-800 bg-rose-50",
};

export default function ClientHealthCard({ project }) {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = async () => {
    setLoading(true);
    try {
      const [hc, ev] = await Promise.all([
        api.get(`/projects/${project.id}/health-card`, { params: { days } }),
        api.get(`/projects/${project.id}/escalation-events`,
                  { params: { limit: 8 } }).catch(() => ({ data: { rows: [] } })),
      ]);
      setData(hc.data);
      setEvents(ev.data.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load health card")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (project?.id) load(); }, [project?.id, days]);

  if (loading || !data) {
    return (
      <div className="border border-border bg-card p-4 text-xs text-muted-foreground"
                data-testid="client-health-card">
        <Loader2 className="w-4 h-4 animate-spin inline-block mr-1.5" />
        Loading client health card…
      </div>
    );
  }

  const sla = data.deliverable_sla || {};
  const stake = data.stakeholders || {};
  const events_kpi = data.escalation_events || {};

  return (
    <div className="border-2 border-primary/40 bg-card" data-testid="client-health-card">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2 bg-muted/20">
        <HeartPulse className="w-4 h-4 text-primary" />
        <span className="text-[11px] uppercase tracking-widest font-bold text-primary">
          Client health card · last {data.days}d
        </span>
        <span className="text-[10px] text-muted-foreground">
          Auto-emailed to internal stakeholders every Friday 09:00 UTC
        </span>
        <div className="ml-auto flex items-center gap-1">
          {[7, 14, 30].map(w => (
            <button key={w} type="button" onClick={() => setDays(w)}
                      className={`h-6 px-2 text-[10px] border ${days === w ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}
                      data-testid={`health-card-window-${w}`}>
              {w}d
            </button>
          ))}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3">
        <KpiTile icon={Users} label="Stakeholders"
                   primary={stake.total}
                   sub={`${stake.by_type?.internal || 0} internal · ${stake.by_type?.client || 0} client · ${stake.by_type?.external || 0} ext`}
                   tone="sky"
                   testid="health-kpi-stakeholders" />
        <KpiTile icon={ShieldAlert} label="Escalation events"
                   primary={events_kpi.total}
                   sub={events_kpi.total
                     ? Object.entries(events_kpi.by_level || {}).map(([l, c]) => `L${l}·${c}`).join(" · ")
                     : "No breaches"}
                   tone={events_kpi.total ? "amber" : "emerald"}
                   testid="health-kpi-escalations" />
        <KpiTile icon={Package} label="Deliverable SLA"
                   primary={`${sla.breached || 0}`}
                   sub={`${sla.at_risk || 0} at risk · ${sla.on_track || 0} on track · ${sla.shipped || 0} shipped`}
                   tone={(sla.breached || 0) > 0 ? "rose" : "emerald"}
                   testid="health-kpi-sla" />
        <KpiTile icon={ClipboardCheck} label="Health"
                   primary={data.health ? data.health.replace("_", " ") : "—"}
                   sub={data.status || "active"}
                   tone={data.health === "on_track" ? "emerald" : data.health === "at_risk" ? "amber" : data.health === "off_track" ? "rose" : "muted"}
                   testid="health-kpi-status" />
      </div>

      {/* Escalation events + Last client edit */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-3 p-3 pt-0">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
            Recent escalation events
          </div>
          {events.length === 0 ? (
            <div className="text-[11px] text-muted-foreground italic border border-dashed border-border p-3 text-center">
              No escalation events in this window — smooth sailing.
            </div>
          ) : (
            <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
              {events.map(ev => (
                <div key={ev.id} className="border border-border border-l-4 border-l-amber-500 bg-amber-50/40 p-2"
                          data-testid={`health-event-${ev.id}`}>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-mono font-bold text-amber-800 bg-amber-100 border border-amber-300 px-1">L{ev.level}</span>
                    <span className="text-xs font-medium truncate flex-1">
                      {ev.task_title || "Task"}
                    </span>
                    <span className="text-[10px] text-muted-foreground">{fmtRelative(ev.fired_at)}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 pl-6">
                    {ev.actor_name || "?"} · {ev.hours_overdue}h overdue (threshold: {ev.sla_threshold_hrs}h)
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
            Last client edit
          </div>
          {data.last_client_edit ? (
            <div className="border border-border p-2 bg-violet-50/50">
              <div className="flex items-center gap-1.5">
                {data.last_client_edit.kind === "carry_over"
                  ? <ArrowLeftRight className="w-3.5 h-3.5 text-violet-600" />
                  : <PenSquare className="w-3.5 h-3.5 text-amber-600" />}
                <span className="text-xs font-semibold truncate">
                  {data.last_client_edit.actor_name}
                </span>
                <span className="text-[10px] text-muted-foreground ml-auto">
                  {fmtRelative(data.last_client_edit.at)}
                </span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Section: <b className="text-foreground">{data.last_client_edit.section === "*all*" ? "All" : (data.last_client_edit.section || "?")}</b>
                {data.last_client_edit.version && ` · v${data.last_client_edit.version}`}
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-muted-foreground italic border border-dashed border-border p-3 text-center">
              No client edits in this window.
            </div>
          )}

          {data.status_updates && data.status_updates.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mt-3 mb-1">
                Latest status updates
              </div>
              <div className="space-y-1">
                {data.status_updates.slice(0, 3).map((u, i) => (
                  <div key={i} className="border border-border bg-white p-2 text-[11px]">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="font-medium truncate">{u.created_by_name || "—"}</span>
                      <span className="text-[9px] text-muted-foreground">{fmtRelative(u.created_at)}</span>
                    </div>
                    <div className="text-muted-foreground line-clamp-2">{u.summary}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiTile({ icon: Icon, label, primary, sub, tone, testid }) {
  const t = {
    sky: "border-sky-400 bg-sky-50 text-sky-900",
    amber: "border-amber-400 bg-amber-50 text-amber-900",
    rose: "border-rose-400 bg-rose-50 text-rose-900",
    emerald: "border-emerald-400 bg-emerald-50 text-emerald-900",
    muted: "border-border bg-card",
  }[tone] || "border-border bg-card";
  return (
    <div className={`border p-2 ${t}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest font-bold flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-2xl font-mono font-black leading-tight capitalize">{primary ?? "—"}</div>
      <div className="text-[9px] opacity-80 mt-0.5">{sub}</div>
    </div>
  );
}
